import { faExclamationCircle, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { hasValue, styles } from "@repo/helpers";
import { useCallback, useEffect, useMemo, useRef } from "react";

import style from "./style.module.scss";
import { BaseInputMarkupProps } from "./types";

export function InputComponent(props: BaseInputMarkupProps) {
	const {
		id,
		label,
		includeError,
		hideAddon,
		hideLabel,
		value,
		validationError,
		leftAddon,
		rightAddon,
		className,
		addOnClassName,
		hideFormGroupClass,
		hideInputGroupClass,
		inline,
		flexInputGroup,
		children,
		validator,
		setRef,
		clear,
		componentStyles,
	} = props;

	const ref = useRef(null);

	useEffect(() => {
		if (ref && setRef) setRef(ref);
	}, [ref, setRef]);

	const error = useMemo(
		() => validationError || (validator && validator(value)),
		[value, validator, validationError],
	);

	const fullError = useMemo(() => props.error ?? error, [props.error, error]);
	const formGroupClass = useMemo(
		() => styles(!hideFormGroupClass && style.formGroup, inline && style.inline, className),
		[className, inline, hideFormGroupClass],
	);
	const inputGroupClass = useMemo(
		() =>
			styles(
				!hideInputGroupClass && style.inputGroup,
				flexInputGroup && style.flexInputGroup,
				fullError && style.error,
			),
		[fullError, flexInputGroup, hideInputGroupClass],
	);
	const labelStyle = useMemo(
		() => styles(style.controlLabel, componentStyles, inline && style.inlineLabel),
		[inline, componentStyles],
	);

	const InputLabel = useCallback(() => {
		if (hideLabel) return null;
		return (
			<label htmlFor={id} className={labelStyle}>
				{label}
			</label>
		);
	}, [hideLabel, id, labelStyle, label]);

	const AddOn = useCallback(() => {
		if (!hasValue(fullError) || hideAddon) return null;
		return (
			<span className={styles(addOnClassName, style.inputGroupAddon, style.error)}>
				<FontAwesomeIcon icon={faExclamationCircle} title={fullError} />
			</span>
		);
	}, [fullError, hideAddon, addOnClassName]);

	const ClearButton = useCallback(() => {
		if (!clear) return null;
		return (
			<button disabled={props.disabled} className={styles(style.inputGroupAddon)} onClick={clear}>
				<FontAwesomeIcon icon={faTimes} title="Clear" />
			</button>
		);
	}, [clear, props.disabled]);

	const AddonLeft = useCallback(() => {
		if (!leftAddon) return null;
		return <span className={style.inputGroupAddon}>{leftAddon}</span>;
	}, [leftAddon]);

	const AddonRight = useCallback(() => {
		if (!rightAddon) return null;
		return <span className={style.inputGroupAddon}>{rightAddon}</span>;
	}, [rightAddon]);

	const Error = useCallback(() => {
		if (!(includeError && fullError)) return null;
		return <p className={style.error}>{fullError}</p>;
	}, [includeError, fullError]);

	if (props.hidden) return null;

	return (
		<div className={formGroupClass} ref={ref}>
			<InputLabel />
			<div className={inputGroupClass}>
				<AddonLeft />
				{children}
				<AddonRight />
				<AddOn />
				<ClearButton />
			</div>
			<Error />
		</div>
	);
}

export * from "./types";
